import { z } from 'zod';
import type { MetricResult } from '@/domain/metrics/contracts';

export const DOCUMENTATION_SOURCES = ['derived', 'manual', 'ai_assisted'] as const;
export type DocumentationSource = (typeof DOCUMENTATION_SOURCES)[number];

export const DocumentationSourceSchema = z.enum(DOCUMENTATION_SOURCES);
export const TextOverrideSchema = z.object({
  content: z.string().max(20_000),
  source: DocumentationSourceSchema,
  sourceFingerprint: z.string().min(1),
});
export type TextOverride = z.infer<typeof TextOverrideSchema>;

export const DocumentationObjectiveSchema = z.object({
  objective: z.string().max(500),
  metric: z.string().max(80).optional(),
  target: z.number().finite().optional(),
  unit: z.string().max(40).optional(),
  period: z.string().max(80).optional(),
  notes: z.string().max(2_000).optional(),
  source: DocumentationSourceSchema,
  sourceFingerprint: z.string().min(1),
});
export type DocumentationObjective = z.infer<typeof DocumentationObjectiveSchema>;

export const SopStepOverlaySchema = z.object({
  nodeId: z.string().min(1),
  title: z.string().max(300).optional(),
  description: z.string().max(4_000).optional(),
  expectedOutcome: z.string().max(1_000).optional(),
  order: z.number().int().nonnegative().optional(),
  source: DocumentationSourceSchema.default('manual'),
  sourceFingerprint: z.string().min(1),
});

export const SopOverlaySchema = z.object({
  title: z.string().max(300).optional(),
  purpose: z.string().max(2_000).optional(),
  prerequisites: z.array(z.string().max(500)).max(50).optional(),
  exceptions: z.array(z.string().max(1_000)).max(50).optional(),
  escalation: z.string().max(2_000).optional(),
  notes: z.string().max(4_000).optional(),
  steps: z.array(SopStepOverlaySchema).max(500).optional(),
  source: DocumentationSourceSchema.default('manual'),
  sourceFingerprint: z.string().min(1),
});

export const ChecklistItemOverlaySchema = z.object({
  id: z.string().min(1),
  stepNodeId: z.string().optional(),
  label: z.string().trim().min(1).max(500),
  required: z.boolean().default(true),
  order: z.number().int().nonnegative(),
  custom: z.boolean().default(false),
});

export const KpiConfigSchema = z.object({
  id: z.string().min(1),
  included: z.boolean().optional(),
  label: z.string().max(120).optional(),
  note: z.string().max(1_000).optional(),
  owner: z.string().max(200).optional(),
  target: z.number().finite().optional(),
  targetPeriod: z.string().max(80).optional(),
});

export const DocumentationGraphExtensionSchema = z.object({
  version: z.literal(1).default(1),
  overviewOverride: TextOverrideSchema.optional(),
  objective: DocumentationObjectiveSchema.optional(),
  sop: SopOverlaySchema.optional(),
  checklistItems: z.array(ChecklistItemOverlaySchema).max(500).optional(),
  kpis: z.array(KpiConfigSchema).max(100).optional(),
  includeAttentionPoints: z.boolean().default(true),
  export: z
    .object({
      clientName: z.string().max(200).optional(),
      branding: z.enum(['dark_funnels', 'client', 'minimal']).default('dark_funnels'),
    })
    .default({ branding: 'dark_funnels' }),
});
export type DocumentationGraphExtension = z.infer<typeof DocumentationGraphExtensionSchema>;

export const DEFAULT_DOCUMENTATION_EXTENSION: DocumentationGraphExtension = {
  version: 1,
  includeAttentionPoints: true,
  export: { branding: 'dark_funnels' },
};

export const DOCUMENTATION_WARNING_CODES = [
  'missing_objective',
  'missing_responsible',
  'missing_system',
  'missing_kpi',
  'missing_stage_description',
  'ambiguous_branch',
  'cycle_documentation_limited',
  'diagnosis_low_coverage',
  'attachment_not_exported',
  'stale_override',
] as const;
export type DocumentationWarningCode = (typeof DOCUMENTATION_WARNING_CODES)[number];

export interface DocumentationWarning {
  code: DocumentationWarningCode;
  message: string;
  refs?: readonly string[];
}

export interface DocumentationNodeInput {
  id: string;
  type: string;
  name: string;
  description?: string;
  responsible?: string;
  status?: string;
  category?: string;
  systemName?: string;
  metrics: Record<string, MetricResult<unknown>>;
  goal?: { metric: string; value: number; period?: string };
  hasAttachments?: boolean;
}

export interface DocumentationEdgeInput {
  id: string;
  source: string;
  target: string;
  label?: string;
  condition?: string;
}

export interface DocumentationContext {
  funnel: { name: string; description?: string | null; ownerName?: string };
  nodes: DocumentationNodeInput[];
  edges: DocumentationEdgeInput[];
  funnelMetrics: Record<string, MetricResult<unknown>>;
  scenarioTarget?: { metric: string; value: number; period?: string };
  diagnosis?: { title: string; severity: string; evidence: string[] }[];
  diagnosisCoverage?: number;
  config?: DocumentationGraphExtension;
}

export interface DocumentationSection<T = unknown> {
  id: string;
  type: string;
  title: string;
  content: T;
  source: DocumentationSource;
  order: number;
  visible: boolean;
  stale?: boolean;
}

export interface DocumentationSystem {
  id: string;
  name: string;
  type: string;
  role: string;
  nodeRefs: string[];
  source: DocumentationSource;
}

export interface DocumentationKpi {
  id: string;
  scope: 'funnel' | 'stage';
  metric: string;
  label: string;
  current: MetricResult<unknown>;
  target?: number;
  unit: string;
  period?: string;
  owner?: string;
  note?: string;
  nodeRefs: string[];
  source: DocumentationSource;
}

export interface SopStep {
  id: string;
  order: number;
  title: string;
  description: string;
  nodeRefs: string[];
  responsible?: string;
  systems: string[];
  expectedOutcome?: string;
  branch?: string;
  source: DocumentationSource;
  stale?: boolean;
}

export interface SopDocument {
  title: string;
  purpose: string;
  prerequisites: string[];
  steps: SopStep[];
  exceptions: string[];
  escalation?: string;
  notes?: string;
  source: DocumentationSource;
  stale?: boolean;
}

export interface ChecklistItem {
  id: string;
  label: string;
  required: boolean;
  order: number;
  nodeRefs: string[];
  source: DocumentationSource;
}

export interface GeneratedDocumentation {
  sections: DocumentationSection[];
  overview: DocumentationSection<string>;
  objective: DocumentationSection<DocumentationObjective | null>;
  stages: DocumentationSection<DocumentationNodeInput[]>;
  responsibilities: DocumentationSection<
    Array<{ responsible: string; nodeRefs: string[]; stages: string[] }>
  >;
  systems: DocumentationSection<DocumentationSystem[]>;
  kpis: DocumentationSection<DocumentationKpi[]>;
  sop: DocumentationSection<SopDocument>;
  checklist: DocumentationSection<ChecklistItem[]>;
  attentionPoints?: DocumentationSection<NonNullable<DocumentationContext['diagnosis']>>;
  warnings: DocumentationWarning[];
  coverage: { complete: number; total: number; ratio: number };
  documentOwner?: string;
}
